import { defineComponent as _defineComponent } from 'vue'
import type { ThumborImage, ThumborOptions } from '@/filters/thumbor';
import type { PosterSponsoredRecImages, PosterTitle } from '@/interfaces/components/poster/PosterGraphql';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { ObservableOptions } from '@/interfaces/observable';
import type { AdditionalProperties } from '@/helpers/composables/useBatchImpressionTracking';
import type { WatchNowOfferFragment } from '@/pages/graphql/fragments/WatchNowOffer.fragment';
import type { Maybe } from '@/@types/graphql-types';

import Vue, { computed, ref } from 'vue';
import VueObserveVisibility from 'vue-observe-visibility';

import { MonetizationType, ObjectType, TitleListType } from '@/@types/graphql-types';

import PictureComp from '@/components/picture/PictureComp.vue';

import { diffInDays, diffInMilliseconds } from '@/helpers/date-helper';
import { usePosterImpressionTracking } from '@/helpers/composables/usePosterImpressionTracking';

import { getDisneyImpressionUrl } from '@/helpers/provider-promotion';
import { isTitleInList, isTitleSeason } from '@/helpers/title-helper';

import { faClock } from '@fortawesome/free-solid-svg-icons';
import { useI18n } from '@/helpers/composables/useI18n';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'PosterGraphql',
  props: {
    additionalContexts: { default: () => [] },
    availabilityBadgeSize: { default: 'long' },
    clickSource: null,
    forceQuickActions: { type: Boolean },
    freeOffersCount: { default: 0 },
    hasMoreLikeThis: { type: Boolean },
    imageProfile: { default: 'poster' },
    index: null,
    lazy: { type: Boolean, default: true },
    loading: { default: 'eager' },
    moduleGroup: { default: '' },
    noRadius: null,
    observableOptions: null,
    showAvailabilityBadge: { type: Boolean },
    showFreeTag: { type: Boolean },
    showNewEpisodesCount: { type: Boolean },
    showQuickActions: { type: Boolean },
    showRibbon: { type: Boolean },
    showTvBadge: { type: Boolean, default: true },
    showWatchNowButton: { type: Boolean, default: true },
    smallBubbles: { type: Boolean },
    sponsoredRecImages: null,
    title: null,
    trackingProperties: { default: () => ({}) },
    watchNowOffer: null
  },
  emits: ["click", "imageError"],
  setup(__props: any) {

const props = __props as { additionalContexts: SnowplowContext[], availabilityBadgeSize: 'short' | 'long', clickSource?: string, forceQuickActions?: boolean, freeOffersCount: number, hasMoreLikeThis?: boolean, imageProfile: keyof ThumborImage<any>, index?: number, lazy: boolean, loading: 'lazy' | 'eager', moduleGroup: string, noRadius?: 'top' | 'right' | 'bottom' | 'left', observableOptions?: ObservableOptions, showAvailabilityBadge?: boolean, showFreeTag?: boolean, showNewEpisodesCount?: boolean, showQuickActions?: boolean, showRibbon?: boolean, showTvBadge: boolean, showWatchNowButton: boolean, smallBubbles?: boolean, sponsoredRecImages?: PosterSponsoredRecImages, title: PosterTitle, trackingProperties: AdditionalProperties, watchNowOffer?: Maybe<WatchNowOfferFragment> };

Vue.use(VueObserveVisibility);

const FreeTag = () => import('./FreeTag.vue');
const ImpressionPixel = () =>
	import(
		/* webpackChunkName: "impression-pixel" */ '@/features/sponsored-recommendations/components/ImpressionPixel.vue'
	);
const AvailabilityBadge = () => import('@/components/poster/AvailabilityBadge.vue');
const PosterQuickActionsGraphql = () => import('@/components/poster/PosterQuickActionsGraphql.vue');
const TitlePosterRibbonGraphqlLite = () => import('@/components/poster/TitlePosterRibbonGraphqlLite.vue');





const { settings, isLoggedIn } = storeToRefs(useUserStore());
const { webLocale } = storeToRefs(useLanguageStore());

const hasFreeOffers = computed(() => {
	if (!props.showFreeTag) {
		return false;
	}

	const hasFreeOffersCount = props.freeOffersCount && props.freeOffersCount > 0;
	const hasFreeMonetizationType =
		props.watchNowOffer &&
		[MonetizationType.Free, MonetizationType.Ads].includes(props.watchNowOffer.monetizationType);

	return hasFreeOffersCount || hasFreeMonetizationType;
});

const posterUrl = computed(() => props.title.content?.posterUrl ?? '');

const titleSlug = computed(() => {
	const fullPath = posterUrl.value?.match(/([^\/]*)$/);
	return fullPath && fullPath[0] ? fullPath[0] : 'Poster';
});

const imageUrl = computed(() => {
	// TODO [PictureComp] Refactor PictureComp to be able to use new url templating /poster/poster_id/{profile}/movie_name.{format}
	return Vue.filter('Thumbor')({
		url: posterUrl.value,
		imageType: props.imageProfile,
	} as ThumborOptions);
});

const showTVShowBadge = computed(
	() => props.showTvBadge && [ObjectType.Show, ObjectType.ShowSeason].includes(props.title.objectType)
);

const leavingOfferFromSelectedPackages = computed(() => {
	const userPackages = settings.value.providers;
	return (
		(props.title?.availableTo ?? [])
			?.filter(
				item =>
					item?.availableCountDown ||
					(!!item?.availableToDate && diffInDays(new Date(item.availableToDate)) < 30)
			)
			.find(item => userPackages.includes(item?.package?.shortName)) || null
	);
});

const upcomingReleaseFromSelectedPackages = computed(() => {
	const userPackages = settings.value.providers;
	return (
		(props.title?.content?.upcomingReleases ?? [])
			?.filter(item => !!item?.releaseDate && diffInMilliseconds(new Date(item.releaseDate)) > 0)
			.filter(item => (item?.releaseCountDown ?? diffInDays(new Date(item?.releaseDate))) < 30)
			.find(item => userPackages.includes(item?.package?.shortName!)) || null
	);
});

const availabilityBadgeDate = computed(() => {
	if (!!upcomingReleaseFromSelectedPackages.value) {
		return upcomingReleaseFromSelectedPackages.value.releaseDate;
	}
	if (!!leavingOfferFromSelectedPackages.value) {
		return leavingOfferFromSelectedPackages.value.availableToDate;
	}
	return null;
});

const isShowAvailabilityBadge = computed(
	() => props.showAvailabilityBadge && isLoggedIn.value && !!availabilityBadgeDate.value
);

const availabilityBadgeType = computed(() => (!!upcomingReleaseFromSelectedPackages.value ? 'upcoming' : 'leaving'));

const availabilityBadgeCountdown = computed(() => {
	if (!!upcomingReleaseFromSelectedPackages.value) {
		return upcomingReleaseFromSelectedPackages.value.releaseCountDown;
	}
	if (!!leavingOfferFromSelectedPackages.value) {
		return leavingOfferFromSelectedPackages.value.availableCountDown;
	}
	return null;
});

const titleSeasonNumber = computed(() => (isTitleSeason(props.title) ? props.title?.content?.seasonNumber : undefined));

const { tc } = useI18n();
const titleName = computed(() =>
	isTitleSeason(props.title)
		? `${props.title?.show?.content?.title} - ${tc('WEBAPP_SEASON')} ${titleSeasonNumber.value}`
		: props.title?.content?.title
);

// IMPRESSION PIXEL //
const mountImpressionPixel = ref(false);

const externalImpressionTag = computed(() =>
	getDisneyImpressionUrl(props.watchNowOffer?.package?.shortName, webLocale.value)
);

const { onVisibilityChanged } = usePosterImpressionTracking({
	groupId: () => props.moduleGroup ?? '',
	preventDuplicateAdditionalContext: true,
	additionalContexts: () => props.additionalContexts!,
	additionalProperties: () => props.trackingProperties,
});

function onVisibilityCallback() {
	if (!mountImpressionPixel.value && externalImpressionTag.value) {
		mountImpressionPixel.value = true;
	}
}

// IMPRESSION PIXEL END //

return { __sfc: true,FreeTag, ImpressionPixel, AvailabilityBadge, PosterQuickActionsGraphql, TitlePosterRibbonGraphqlLite, props, settings, isLoggedIn, webLocale, hasFreeOffers, posterUrl, titleSlug, imageUrl, showTVShowBadge, leavingOfferFromSelectedPackages, upcomingReleaseFromSelectedPackages, availabilityBadgeDate, isShowAvailabilityBadge, availabilityBadgeType, availabilityBadgeCountdown, titleSeasonNumber, tc, titleName, mountImpressionPixel, externalImpressionTag, onVisibilityChanged, onVisibilityCallback, TitleListType, PictureComp, isTitleInList, faClock }
}

})